import { useEffect, useMemo, useState } from 'react';
import type { Fund } from '../types';
import { listFunds } from '../lib/api';
import { IconSearch, IconPlus, IconChevronRight, IconRefresh, IconBookmark, IconClose } from '../components/icons';
import { initials, logoColor, relativeDate } from '../lib/format';
import { SkeletonRows } from '../components/Skeleton';
import { useToast } from '../lib/toast';
import { InvestorEditDrawer } from './InvestorEditDrawer';

interface Props {
  onCountChange?: (n: number) => void;
  onDirty?: (n: number) => void;
}

/* ----- Filter vocabularies (mirror the public site) ----- */
const STAGES = ['Pre-Seed', 'Seed', 'Series A', 'Series B', 'Series C', 'Growth', 'Late stage'] as const;
const CONVICTIONS = ['Pure-play', 'Deep tech actif', 'Opportuniste', 'CVC stratégique', 'Dormant quantum'] as const;
const TYPES = ['VC Firm', 'Corporate VC', 'PE Firm', 'Family Office'] as const;
const SHORTLIST = ['Approved', 'Draft'] as const;

// Quantum fit priority order: matches the public directory's default sort.
// Pure-play first, dormant last.
const CONVICTION_ORDER: Record<string, number> = {
  'Pure-play':       0,
  'Deep tech actif': 1,
  'Opportuniste':    2,
  'CVC stratégique': 3,
  'Dormant quantum': 4,
};

/* ----- Ticket buckets ----- */
type TicketBand = { key: string; label: string; min: number; max: number };
const TICKET_BANDS: TicketBand[] = [
  { key: 'xs', label: '< $500K',      min: 0,         max: 500_000 },
  { key: 's',  label: '$500K – $2M',  min: 500_000,   max: 2_000_000 },
  { key: 'm',  label: '$2M – $10M',   min: 2_000_000, max: 10_000_000 },
  { key: 'l',  label: '$10M – $50M',  min: 10_000_000,max: 50_000_000 },
  { key: 'xl', label: '> $50M',       min: 50_000_000,max: Number.POSITIVE_INFINITY },
];

function fundMatchesBand(f: Fund, band: TicketBand): boolean {
  const lo = typeof f.ticketMin === 'number' && f.ticketMin > 0 ? f.ticketMin : null;
  const hi = typeof f.ticketMax === 'number' && f.ticketMax > 0 ? f.ticketMax : null;
  if (lo === null && hi === null) return false;
  const fl = lo ?? hi!;
  const fh = hi ?? lo!;
  return fl <= band.max && fh >= band.min;
}

/* ----- Quantum conviction badge styling ----- */
const CONVICTION_STYLE: Record<string, string> = {
  'Pure-play':         'bg-funds-soft text-funds-deep',
  'Deep tech actif':   'bg-amber-soft text-amber',
  'Opportuniste':      'bg-cream-deep text-navy',
  'CVC stratégique':   'bg-founders-soft text-founders-deep',
  'Dormant quantum':   'bg-red-50 text-red-700',
};

/* ----- Column definitions ----- */
type ColKey = 'fund' | 'stage' | 'conviction' | 'focus' | 'type' | 'thesisEn' | 'thesisFr' | 'portfolio' | 'country' | 'ticket' | 'shortlist' | 'updated';
type SortKey = 'name' | 'country' | 'type' | 'quantumConviction' | 'ticketMax' | 'shortlist' | 'updatedAt';

interface ColDef {
  key: ColKey;
  label: string;
  defaultWidth: number;
  sortKey?: SortKey;
  align?: 'left' | 'right';
}

// Column order matches the user's spec (Stage, Quantum fit, Focus, Fund type,
// Thesis EN, Thesis FR, Portfolio, Country) plus the admin utility columns
// at the end (Ticket, Shortlist, Updated). Fund stays first as anchor.
const COLUMNS: ColDef[] = [
  { key: 'fund',       label: 'Fund',         defaultWidth: 260, sortKey: 'name' },
  { key: 'stage',      label: 'Stage',        defaultWidth: 140 },
  { key: 'conviction', label: 'Quantum fit',  defaultWidth: 140, sortKey: 'quantumConviction' },
  { key: 'focus',      label: 'Focus',        defaultWidth: 200 },
  { key: 'type',       label: 'Fund type',    defaultWidth: 120, sortKey: 'type' },
  { key: 'thesisEn',   label: 'Thesis EN',    defaultWidth: 320 },
  { key: 'thesisFr',   label: 'Thesis FR',    defaultWidth: 320 },
  { key: 'portfolio',  label: 'Portfolio',    defaultWidth: 220 },
  { key: 'country',    label: 'Country',      defaultWidth: 130, sortKey: 'country' },
  { key: 'ticket',     label: 'Ticket',       defaultWidth: 120, sortKey: 'ticketMax', align: 'right' },
  { key: 'shortlist',  label: 'Shortlist',    defaultWidth: 110, sortKey: 'shortlist' },
  { key: 'updated',    label: 'Updated',      defaultWidth: 110, sortKey: 'updatedAt' },
];

const STORAGE_WIDTHS = 'admin.investors.colWidths.v1';
const STORAGE_SEGMENTS = 'admin.investors.segments.v1';

interface FilterState {
  search: string;
  stage: string;
  conviction: string;
  focus: string;
  country: string;
  ticket: string;
  type: string;
  shortlist: string;
}
interface Segment {
  name: string;
  filters: FilterState;
}

/* ----- Logo with graceful fallback ----- */
function slugify(name: string): string {
  return (name || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function FundLogo({ fund }: { fund: Fund }) {
  const [errored, setErrored] = useState(false);
  const slug = fund.slug || slugify(fund.name);
  // Try the explicit logoUrl if set in Airtable, fall back to the public
  // site's bundled logo (PNG keyed by slug). If both fail, show the colored
  // initials avatar so the row still has a visual anchor.
  const url = fund.logoUrl || (slug ? `https://financeforquantum.com/assets/fund-logos/${slug}.png` : '');
  const c = logoColor(fund.name);
  if (errored || !url) {
    return <div className="w-8 h-8 rounded-md grid place-items-center font-display text-[12px] shrink-0" style={{ background: c.bg, color: c.fg }}>{initials(fund.name)}</div>;
  }
  return (
    <img
      src={url}
      alt={fund.name}
      onError={() => setErrored(true)}
      className="w-8 h-8 rounded-md object-contain bg-white border border-line shrink-0"
      loading="lazy"
    />
  );
}

/* ----- Truncate text for inline preview (thesis) ----- */
function thesisPreview(text: string | undefined, max = 120): string {
  if (!text) return '';
  // Drop bullet markers and take the first paragraph for preview.
  const firstPara = text.replace(/^[\-•*]\s*/gm, '').split(/\n\s*\n/)[0].replace(/\s+/g, ' ').trim();
  if (firstPara.length <= max) return firstPara;
  return firstPara.slice(0, max) + '…';
}

export function InvestorsPage({ onCountChange, onDirty }: Props) {
  const [funds, setFunds] = useState<Fund[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [convictionFilter, setConvictionFilter] = useState('');
  const [focusFilter, setFocusFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [ticketFilter, setTicketFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [shortlistFilter, setShortlistFilter] = useState('');
  const [editing, setEditing] = useState<Fund | null>(null);
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());
  const toast = useToast();

  // Saved filter segments, persisted in localStorage. The Investors view is
  // the place admins re-apply the same filter combos (e.g. "Pure-play EU
  // Series A") most often, so naming and recalling them is a real win.
  const [segments, setSegments] = useState<Segment[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_SEGMENTS);
      return raw ? (JSON.parse(raw) as Segment[]) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_SEGMENTS, JSON.stringify(segments)); } catch { /* ignore */ }
  }, [segments]);

  function currentFilters(): FilterState {
    return {
      search, stage: stageFilter, conviction: convictionFilter, focus: focusFilter,
      country: countryFilter, ticket: ticketFilter, type: typeFilter, shortlist: shortlistFilter,
    };
  }
  function applyFilters(f: FilterState) {
    setSearch(f.search); setStageFilter(f.stage); setConvictionFilter(f.conviction);
    setFocusFilter(f.focus); setCountryFilter(f.country); setTicketFilter(f.ticket);
    setTypeFilter(f.type); setShortlistFilter(f.shortlist);
  }
  function saveCurrentSegment() {
    const name = prompt('Name this filter segment:', '');
    if (!name) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    if (segments.some(s => s.name === trimmed)) {
      if (!confirm(`Replace existing "${trimmed}"?`)) return;
    }
    const next = [...segments.filter(s => s.name !== trimmed), { name: trimmed, filters: currentFilters() }];
    setSegments(next);
    toast.success('Segment saved', trimmed);
  }
  function deleteSegment(name: string) {
    setSegments(prev => prev.filter(s => s.name !== name));
  }

  // Default sort: by Quantum fit ascending (Pure-play first), matching the
  // public directory's default ordering. Click any header to override.
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'quantumConviction', dir: 'asc' });
  function toggleSort(key: SortKey) {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'updatedAt' || key === 'ticketMax' ? 'desc' : 'asc' });
  }

  // Resizable column widths, persisted to localStorage so layout sticks
  // across reloads and devices.
  const [widths, setWidths] = useState<Record<ColKey, number>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_WIDTHS);
      if (saved) return { ...defaultWidths(), ...JSON.parse(saved) };
    } catch { /* ignore */ }
    return defaultWidths();
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_WIDTHS, JSON.stringify(widths)); } catch { /* ignore */ }
  }, [widths]);

  function startResize(key: ColKey, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widths[key];
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      const newW = Math.max(80, Math.min(600, startW + (ev.clientX - startX)));
      setWidths(prev => ({ ...prev, [key]: newW }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  function resetWidths() {
    setWidths(defaultWidths());
  }

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await listFunds();
      setFunds(res.funds);
      onCountChange?.(res.count);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load funds';
      setError(msg);
      toast.error('Failed to load funds', msg);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Dynamic options derived from data
  const countries = useMemo(() => {
    const set = new Set<string>();
    for (const f of funds) if (f.country) set.add(f.country);
    return Array.from(set).sort();
  }, [funds]);

  const focuses = useMemo(() => {
    const tally = new Map<string, number>();
    for (const f of funds) for (const s of (f.sectors ?? [])) tally.set(s, (tally.get(s) ?? 0) + 1);
    return Array.from(tally.entries()).sort((a, b) => b[1] - a[1]).map(([s]) => s);
  }, [funds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return funds.filter(f => {
      if (q && !`${f.name} ${f.city ?? ''} ${f.country ?? ''}`.toLowerCase().includes(q)) return false;
      if (stageFilter && !(f.stages ?? []).includes(stageFilter)) return false;
      if (convictionFilter && f.quantumConviction !== convictionFilter) return false;
      if (focusFilter && !(f.sectors ?? []).includes(focusFilter)) return false;
      if (countryFilter && f.country !== countryFilter) return false;
      if (ticketFilter) {
        const band = TICKET_BANDS.find(b => b.key === ticketFilter);
        if (band && !fundMatchesBand(f, band)) return false;
      }
      if (typeFilter && f.type !== typeFilter) return false;
      if (shortlistFilter && f.shortlist !== shortlistFilter) return false;
      return true;
    });
  }, [funds, search, stageFilter, convictionFilter, focusFilter, countryFilter, ticketFilter, typeFilter, shortlistFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sort.dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      // Special case: sort by quantum fit uses the canonical priority order,
      // not lexicographic. That's what the public directory does.
      if (sort.key === 'quantumConviction') {
        const oa = CONVICTION_ORDER[a.quantumConviction ?? ''] ?? 99;
        const ob = CONVICTION_ORDER[b.quantumConviction ?? ''] ?? 99;
        if (oa !== ob) return (oa - ob) * dir;
        return (a.name || '').localeCompare(b.name || '') * dir;
      }
      const va = (a as unknown as Record<string, unknown>)[sort.key];
      const vb = (b as unknown as Record<string, unknown>)[sort.key];
      const aMissing = va === undefined || va === null || va === '';
      const bMissing = vb === undefined || vb === null || vb === '';
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
    });
    return arr;
  }, [filtered, sort]);

  function clearFilters() {
    setSearch(''); setStageFilter(''); setConvictionFilter(''); setFocusFilter('');
    setCountryFilter(''); setTicketFilter(''); setTypeFilter(''); setShortlistFilter('');
  }
  const activeFilterCount = [stageFilter, convictionFilter, focusFilter, countryFilter, ticketFilter, typeFilter, shortlistFilter].filter(Boolean).length;

  function onSaved(updated: Fund) {
    setFunds(prev => prev.map(f => (f.id === updated.id ? updated : f)));
    setDirtyIds(prev => {
      const next = new Set(prev);
      next.add(updated.id);
      onDirty?.(next.size);
      return next;
    });
    setEditing(null);
  }
  function onDeleted(id: string) {
    setFunds(prev => prev.filter(f => f.id !== id));
    setDirtyIds(prev => {
      const next = new Set(prev);
      next.add(id);
      onDirty?.(next.size);
      return next;
    });
    setEditing(null);
    onCountChange?.(funds.length - 1);
  }

  const totalWidth = COLUMNS.reduce((sum, c) => sum + (widths[c.key] ?? c.defaultWidth), 0);

  return (
    <section className="px-4 md:px-8 py-5 md:py-7">
      <div className="flex items-start md:items-end justify-between mb-4 md:mb-5 gap-3">
        <div className="min-w-0">
          <h1 className="h-display text-[22px] md:text-[26px]">Investors</h1>
          <p className="text-[12.5px] md:text-[13.5px] text-ink-muted mt-0.5 hidden sm:block">
            {funds.length} funds · sorted by quantum fit · edits stay in draft until you click <b>Publish</b>.
          </p>
          <p className="text-[12px] text-ink-muted mt-0.5 sm:hidden">{funds.length} funds</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={resetWidths} className="hidden md:block h-9 px-3 text-[13px] rounded-md border border-line bg-white hover:bg-cream-tint text-ink-muted">
            Reset columns
          </button>
          <button onClick={refresh} className="h-9 w-9 md:w-auto md:px-3 text-[13px] rounded-md border border-line bg-white active:bg-cream-tint flex items-center justify-center gap-1.5">
            <IconRefresh className={'w-4 h-4 text-ink-muted ' + (loading ? 'animate-spin' : '')} />
            <span className="hidden md:inline">Refresh</span>
          </button>
          <button
            disabled
            title="Coming in V2"
            className="hidden md:flex h-9 px-3 text-[13px] rounded-md border border-line bg-white text-ink-faint items-center gap-1.5 opacity-60 cursor-not-allowed"
          >
            <IconPlus className="w-4 h-4 text-ink-faint" />
            Add fund
          </button>
        </div>
      </div>

      <div className="bg-white border border-line rounded-lg shadow-card mb-4">
        <div className="px-4 py-3 flex items-center gap-2 flex-wrap border-b border-line-soft">
          <div className="relative">
            <IconSearch className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filter funds…"
              className="h-8 w-[220px] pl-8 pr-2.5 text-[13px] rounded-md border border-line bg-cream-tint focus:bg-white ring-input"
            />
          </div>
          <span className="h-5 w-px bg-line-soft mx-1" />
          <Select value={stageFilter} setValue={setStageFilter} label="Stage" options={STAGES as readonly string[]} />
          <Select value={convictionFilter} setValue={setConvictionFilter} label="Quantum fit" options={CONVICTIONS as readonly string[]} />
          <Select value={focusFilter} setValue={setFocusFilter} label="Focus" options={focuses} />
          <Select value={countryFilter} setValue={setCountryFilter} label="Country" options={countries} />
          <Select value={ticketFilter} setValue={setTicketFilter} label="Ticket" options={TICKET_BANDS.map(b => b.key)} renderOption={k => TICKET_BANDS.find(b => b.key === k)?.label ?? k} />
          <Select value={typeFilter} setValue={setTypeFilter} label="Fund type" options={TYPES as readonly string[]} />
          <span className="h-5 w-px bg-line-soft mx-1" />
          <Select value={shortlistFilter} setValue={setShortlistFilter} label="Shortlist" options={SHORTLIST as readonly string[]} />
          {activeFilterCount > 0 && (
            <>
              <button onClick={clearFilters} className="h-8 px-2.5 text-[12.5px] rounded-md text-ink-muted hover:text-navy hover:bg-cream-tint">
                Clear ({activeFilterCount})
              </button>
              <button onClick={saveCurrentSegment} className="h-8 px-2.5 text-[12.5px] rounded-md text-navy hover:bg-cream-tint flex items-center gap-1.5" title="Save these filters as a named segment">
                <IconBookmark className="w-3.5 h-3.5" />
                Save segment
              </button>
            </>
          )}
          <span className="ml-auto text-[12px] text-ink-muted num">{filtered.length} of {funds.length}</span>
        </div>

        {/* Saved segments chips: re-apply a filter combo with one click. */}
        {segments.length > 0 && (
          <div className="px-4 py-2 flex items-center gap-1.5 flex-wrap border-b border-line-soft text-[12px] bg-cream-tint/40">
            <span className="text-ink-muted text-[11px] uppercase tracking-wider mr-1">Segments:</span>
            {segments.map(s => (
              <span key={s.name} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full border border-line bg-white hover:bg-cream-tint">
                <button onClick={() => applyFilters(s.filters)} className="text-ink font-medium">{s.name}</button>
                <button onClick={() => deleteSegment(s.name)} className="w-4 h-4 grid place-items-center rounded-full text-ink-faint hover:text-red-700 hover:bg-red-50" title={`Delete "${s.name}"`}>
                  <IconClose className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="hidden md:block overflow-x-auto scroll-area">
          {error && <div className="px-4 py-3 text-red-700 bg-red-50 text-[13px]">Error: {error}</div>}
          <table className="text-[13px] table-fixed border-separate" style={{ borderSpacing: 0, width: totalWidth, minWidth: '100%' }}>
            <colgroup>
              {COLUMNS.map(c => <col key={c.key} style={{ width: widths[c.key] ?? c.defaultWidth }} />)}
            </colgroup>
            <thead className="bg-cream-tint text-[11.5px] uppercase tracking-wider text-ink-muted">
              <tr>
                {COLUMNS.map((c, idx) => (
                  <th
                    key={c.key}
                    style={idx === 0 ? { boxShadow: '1px 0 0 0 #EEEAE1' } : undefined}
                    className={
                      'relative font-medium px-3 py-2.5 border-b border-line-soft '
                      + (c.align === 'right' ? 'text-right' : 'text-left')
                      + (idx === 0 ? ' sticky left-0 z-[2] bg-cream-tint' : '')
                    }
                  >
                    {c.sortKey
                      ? <SortButton sortKey={c.sortKey} sort={sort} onSort={toggleSort}>{c.label}</SortButton>
                      : <span>{c.label}</span>}
                    <span
                      onMouseDown={e => startResize(c.key, e)}
                      className="absolute top-1/2 right-0 -translate-y-1/2 w-1 h-5 cursor-col-resize hover:bg-navy/40 transition rounded-full"
                      title="Drag to resize"
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line-soft">
              {loading && funds.length === 0 && (
                <SkeletonRows rows={8} cols={COLUMNS.length} />
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={COLUMNS.length} className="text-center py-12 text-ink-muted">No funds match the current filters.</td></tr>
              )}
              {sorted.map(f => {
                const dirty = dirtyIds.has(f.id);
                const convictionStyle = CONVICTION_STYLE[f.quantumConviction ?? ''] ?? 'bg-cream-deep text-navy';
                const rowBg = dirty ? 'bg-amber-soft/40 hover:bg-amber-soft/60' : 'hover:bg-cream-tint';
                return (
                  <tr
                    key={f.id}
                    onClick={() => setEditing(f)}
                    className={'cursor-pointer transition border-b border-line-soft ' + rowBg}
                  >
                    {/* Fund (logo + name + url) */}
                    <Cell>
                      <div className="flex items-center gap-3 min-w-0">
                        <FundLogo fund={f} />
                        <div className="min-w-0">
                          <div className="font-medium text-ink flex items-center gap-1.5 truncate">
                            {f.name}
                            {dirty && <span className="bg-amber-soft text-amber px-1.5 py-px text-[10px] rounded-full shrink-0">draft</span>}
                          </div>
                          <div className="text-[11.5px] text-ink-muted truncate">{f.website?.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</div>
                        </div>
                        <IconChevronRight className="w-4 h-4 text-ink-faint ml-auto shrink-0" />
                      </div>
                    </Cell>

                    {/* Stage */}
                    <Cell>
                      <ChipList items={f.stages ?? []} max={2} className="bg-funds-soft text-funds-deep" />
                    </Cell>

                    {/* Quantum fit */}
                    <Cell>
                      {f.quantumConviction
                        ? <span className={'inline-flex items-center px-2 py-px rounded-full text-[11.5px] font-medium ' + convictionStyle}>{f.quantumConviction}</span>
                        : <span className="text-ink-faint" />}
                    </Cell>

                    {/* Focus */}
                    <Cell>
                      <ChipList items={f.sectors ?? []} max={2} className="bg-cream-deep text-navy" />
                    </Cell>

                    {/* Fund type */}
                    <Cell className="whitespace-nowrap">{f.type ?? ''}</Cell>

                    {/* Thesis EN */}
                    <Cell>
                      <span className="text-ink-muted" title={f.thesis?.en}>{thesisPreview(f.thesis?.en)}</span>
                    </Cell>

                    {/* Thesis FR */}
                    <Cell>
                      <span className="text-ink-muted" title={f.thesis?.fr}>{thesisPreview(f.thesis?.fr)}</span>
                    </Cell>

                    {/* Portfolio */}
                    <Cell>
                      <ChipList items={f.portfolioNotable ?? []} max={3} className="bg-founders-soft text-founders-deep" />
                    </Cell>

                    {/* Country */}
                    <Cell className="text-ink-muted whitespace-nowrap">{f.country ?? ''}</Cell>

                    {/* Ticket */}
                    <Cell align="right" className="num whitespace-nowrap">{f.ticketUsd ?? f.ticket ?? ''}</Cell>

                    {/* Shortlist */}
                    <Cell>
                      {f.shortlist === 'Approved'
                        ? <span className="inline-flex items-center gap-1 px-2 py-px rounded-full bg-funds-soft text-funds-deep text-[11.5px] font-medium"><span className="w-1.5 h-1.5 rounded-full bg-funds" />Approved</span>
                        : <span className="inline-flex items-center gap-1 px-2 py-px rounded-full bg-cream-deep text-ink text-[11.5px] font-medium">{f.shortlist ?? 'Draft'}</span>}
                    </Cell>

                    {/* Updated */}
                    <Cell className="text-ink-muted whitespace-nowrap">{relativeDate(f.updatedAt)}</Cell>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card list: replaces the wide table on small screens */}
      <div className="md:hidden">
        {error && <div className="px-3 py-2 mb-3 rounded-md text-red-700 bg-red-50 text-[13px] border border-red-200">Error: {error}</div>}
        {loading && funds.length === 0 && (
          <div className="space-y-2.5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white border border-line rounded-lg p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-cream-deep animate-pulse shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 bg-cream-deep rounded animate-pulse" />
                  <div className="h-3 w-48 bg-cream-deep rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-line rounded-lg p-6 text-center text-ink-muted text-[13.5px]">No funds match.</div>
        )}
        <div className="space-y-2.5">
          {sorted.map(f => {
            const dirty = dirtyIds.has(f.id);
            const convictionStyle = CONVICTION_STYLE[f.quantumConviction ?? ''] ?? 'bg-cream-deep text-navy';
            return (
              <button
                key={f.id}
                onClick={() => setEditing(f)}
                className={'w-full text-left bg-white border rounded-lg p-3.5 active:bg-cream-tint transition flex items-start gap-3 ' + (dirty ? 'border-amber/50 bg-amber-soft/30' : 'border-line')}
              >
                <FundLogo fund={f} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium text-ink truncate">{f.name}</span>
                    {dirty && <span className="bg-amber-soft text-amber px-1.5 py-px text-[10px] rounded-full shrink-0">draft</span>}
                  </div>
                  <div className="text-[11.5px] text-ink-muted truncate mt-0.5">
                    {[f.country, f.type, f.ticketUsd ?? f.ticket].filter(Boolean).join(' · ')}
                  </div>
                  {f.quantumConviction && (
                    <span className={'inline-flex items-center px-2 py-px rounded-full text-[10.5px] font-medium mt-2 ' + convictionStyle}>{f.quantumConviction}</span>
                  )}
                </div>
                <IconChevronRight className="w-4 h-4 text-ink-faint mt-1 shrink-0" />
              </button>
            );
          })}
        </div>
      </div>

      {editing && (
        <InvestorEditDrawer
          fund={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
          onDeleted={onDeleted}
        />
      )}
    </section>
  );
}

/* ---------- helpers ---------- */

function defaultWidths(): Record<ColKey, number> {
  return Object.fromEntries(COLUMNS.map(c => [c.key, c.defaultWidth])) as Record<ColKey, number>;
}

function Cell({ children, align = 'left', className = '', style }: { children?: React.ReactNode; align?: 'left' | 'right'; className?: string; style?: React.CSSProperties }) {
  return <td style={style} className={`px-3 py-3 overflow-hidden text-${align} ${className}`}>{children}</td>;
}

function ChipList({ items, max, className }: { items: string[]; max: number; className: string }) {
  if (!items.length) return <span className="text-ink-faint" />;
  return (
    <div className="flex flex-wrap gap-1 overflow-hidden">
      {items.slice(0, max).map(s => (
        <span key={s} className={'inline-flex items-center px-1.5 py-px rounded-full text-[11px] font-medium truncate max-w-full ' + className}>{s}</span>
      ))}
      {items.length > max && <span className="text-[11px] text-ink-faint">+{items.length - max}</span>}
    </div>
  );
}

function SortButton({
  children, sortKey, sort, onSort,
}: {
  children: React.ReactNode;
  sortKey: SortKey;
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  onSort: (key: SortKey) => void;
}) {
  const active = sort.key === sortKey;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onSort(sortKey); }}
      className={'inline-flex items-center gap-1 ' + (active ? 'text-ink' : 'hover:text-ink')}
    >
      {children}
      <span className={'text-[10px] ' + (active ? 'opacity-100' : 'opacity-30')}>
        {active && sort.dir === 'asc' ? '↑' : active && sort.dir === 'desc' ? '↓' : '↕'}
      </span>
    </button>
  );
}

function Select({
  value, setValue, label, options, renderOption,
}: {
  value: string;
  setValue: (v: string) => void;
  label: string;
  options: readonly string[];
  renderOption?: (key: string) => string;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={e => setValue(e.target.value)}
        className={'h-8 pl-2.5 pr-7 text-[12.5px] rounded-md border bg-white hover:bg-cream-tint appearance-none cursor-pointer ' + (value ? 'border-navy/40 text-ink font-medium' : 'border-line')}
      >
        <option value="">{label}: all</option>
        {options.map(o => <option key={o} value={o}>{label}: {renderOption ? renderOption(o) : o}</option>)}
      </select>
      <svg className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-ink-muted" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5}><path d="M6 8l4 4 4-4" /></svg>
    </div>
  );
}
